import { useLayoutEffect, useRef, useState } from 'react';
import {
  Button,
  CheckIcon,
  ClipboardIcon,
  ExportIcon,
  Field,
  GenerateIcon,
} from '@alchemiesolns/ui';
import { CollapsiblePrompt } from './CollapsiblePrompt';
import { CONTEXT_PROMPT_OPTIONS } from '../utils/prompts';
import type { TemplateMode } from './BatchUpload';

interface ExportSectionProps {
  // Template mode
  templateMode: TemplateMode;
  isDevMode: boolean;

  // Alt text state
  altText: string;
  systemPrompt: string;
  contextPrompt: string;
  selectedContextOption: string;
  onAltTextChange: (text: string) => void;
  onSystemPromptChange: (prompt: string) => void;
  onContextPromptChange: (prompt: string) => void;
  onContextOptionSelect: (optionId: string) => void;

  // Alt text generation
  altTextGeneration: {
    generateAltText: () => void | Promise<void>;
    copyAltText: () => void;
    isGenerating: boolean;
    altTextCopied: boolean;
  };

  // Export handler - receives filename and format as parameters
  onExport: (filename: string, format: 'svg' | 'png') => void | Promise<void>;
}

export function ExportSection({
  templateMode,
  isDevMode,
  altText,
  systemPrompt,
  contextPrompt,
  selectedContextOption,
  onAltTextChange,
  onSystemPromptChange,
  onContextPromptChange,
  onContextOptionSelect,
  altTextGeneration,
  onExport,
}: ExportSectionProps) {
  const [exportFormat, setExportFormat] = useState<'svg' | 'png'>('png');
  const [filename, setFilename] = useState('');
  const altTextTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    const el = altTextTextareaRef.current;
    if (!el) return;

    const syncHeight = () => {
      const cs = getComputedStyle(el);
      const minH = parseFloat(cs.minHeight);
      const maxH = parseFloat(cs.maxHeight);
      const minPx = Number.isFinite(minH) && minH > 0 ? minH : 72;
      const maxPx =
        Number.isFinite(maxH) && maxH > 0 ? maxH : Math.min(window.innerHeight * 0.5, 360);
      el.style.height = 'auto';
      const next = Math.min(maxPx, Math.max(minPx, el.scrollHeight));
      el.style.height = `${next}px`;
    };

    syncHeight();
    window.addEventListener('resize', syncHeight);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncHeight) : null;
    ro?.observe(el);
    return () => {
      window.removeEventListener('resize', syncHeight);
      ro?.disconnect();
    };
  }, [altText]);

  const handleContextSelect = (optionId: string) => {
    onContextOptionSelect(optionId);
    // PIPH-130: context prompts are server-resolved by reference name now;
    // the local contextPrompt state is just a dev-mode literal override.
    onContextPromptChange('');
  };

  const handleExport = () => {
    onExport(filename, exportFormat);
  };

  return (
    <div className="build-section">
      <div className="build-side-card build-layout-card">
        <h4 className="panel-side-title build-layout-card-title">Alt Text Description</h4>
        <div className="build-side-card-stack">
          {/* System Prompt - dev mode + reaction mode only */}
          {isDevMode && templateMode !== 'molecule' && (
            <CollapsiblePrompt
              title="System Prompt"
              value={systemPrompt}
              onChange={onSystemPromptChange}
            />
          )}

          {/* Description Style + Generate Button */}
          <div className="build-side-card-stack">
            {templateMode !== 'molecule' && (
              <label className="build-size-label">
                Description Style
              </label>
            )}
            <div className="build-side-card-row">
              {templateMode !== 'molecule' && (
                <div className="build-side-card-row__main">
                  <CollapsiblePrompt
                    title="Description Style"
                    value={contextPrompt}
                    onChange={onContextPromptChange}
                    selectOptions={CONTEXT_PROMPT_OPTIONS}
                    selectedOption={selectedContextOption}
                    onSelectOption={handleContextSelect}
                    selectOnly={!isDevMode}
                  />
                </div>
              )}
              <Button
                variant="primary"
                onClick={altTextGeneration.generateAltText}
                disabled={altTextGeneration.isGenerating}
              >
                <GenerateIcon />
                {altTextGeneration.isGenerating ? 'Generating...' : 'Generate'}
              </Button>
            </div>
          </div>

          <div className="build-side-card-stack">
            <label htmlFor="alt-text-textarea" className="build-size-label">
              Generated Description
            </label>
            <textarea
              ref={altTextTextareaRef}
              id="alt-text-textarea"
              value={altText}
              onChange={(e) => onAltTextChange(e.target.value)}
              className="alt-text-textarea"
              rows={1}
              placeholder="Description will appear here after clicking Generate..."
            />
            <Button
              onClick={altTextGeneration.copyAltText}
              disabled={!altText}
              className="self-start"
            >
              {altTextGeneration.altTextCopied ? (
                <>
                  <CheckIcon />
                  Copied
                </>
              ) : (
                <>
                  <ClipboardIcon />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="build-side-card build-layout-card">
        <h4 className="panel-side-title build-layout-card-title">Export</h4>
        <div className="build-side-card-form-grid">
          <Field label="Filename" htmlFor="filename-input">
            <input
              id="filename-input"
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder={templateMode === 'molecule' ? 'molecule' : 'reaction-scheme'}
              className="filename-input"
            />
          </Field>
          <Field
            label="Format"
            htmlFor="format-select"
            helpLabel="Help choosing export format"
            help="Download as an SVG to support interactive image exploration, an upcoming accessibility feature that will allow students to navigate this image with a screen reader."
          >
            <select
              id="format-select"
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'svg' | 'png')}
              className="template-dropdown"
            >
              <option value="svg">SVG</option>
              <option value="png">PNG</option>
            </select>
          </Field>
        </div>
        <Button variant="primary" onClick={handleExport} className="self-start">
          <ExportIcon />
          Export{' '}
          {filename || (templateMode === 'molecule' ? 'molecule' : 'reaction-scheme')}
        </Button>
      </div>
    </div>
  );
}
